/**
 * taxCardFormat — the three value-level rules a Tax Playground V2 summary card is
 * built on, kept OUT of the SFC so they can be unit-tested under plain Node
 * (see tests/taxCardFormat.test.mjs). `TaxSummaryCardV2.vue` imports them; it must
 * not re-implement any of them.
 *
 * Source of truth: `TaxQuestionV2-IncomeCards-Config.md` §3.1/§4 — measured output of
 * the real node, not a guess.
 *
 * 🔴 RULE 1 (§4 item 1) — `amount` CAN BE null AND MUST NEVER RENDER AS 0.
 * The calc phase does not write back yet, so `headline.amount`, `stats[].amount` and
 * `categories[].amount` arrive as literal `null`. On a tax card "0" reads as "you have
 * no income" — a financial claim about the customer — while `null` only means "not
 * known yet". `formatAmount` therefore answers an em dash for null/undefined/garbage,
 * and there is deliberately NO `|| 0`, `?? 0` or `Number(x) || 0` anywhere in this
 * file: `0` is DATA and must format as "0.00", never collapse into the em dash, and
 * `null` must never be lifted into a zero.
 *
 * 🔴 RULE 2 (§4 item 2) — THE SAME FACT ARRIVES IN TWO SHAPES. `@omitWhenNull` can drop
 * an *object* (so `deduction` / `net` / `netTotal` / `headline` vanish key and all) but
 * cannot drop a *scalar* (so `headline.amount` stays and is null). "Key absent" and
 * "key present holding null" are therefore different states and must be tested
 * differently: `hasObjectRow()` (own-property check) for the object rows,
 * `formatAmount()` for the scalars. They are not interchangeable.
 *
 * 🔴 RULE 3 — `expanded` comes off the wire as a boolean OR as text, so truthiness is
 * decided by `isTruthy()`, not by JS coercion (`Boolean('false') === true`).
 *
 * Pure logic (NO Vue / framework import) so it runs in plain Node.
 */

/** What an unknown amount looks like on screen. Never '0', never ''. */
export const EM_DASH = '—';

/**
 * True only for an OWN key holding a non-null object — see rule 2 above.
 * A missing key, an explicit null, a scalar and an array all answer false, because none
 * of them can supply the `{ label, amount }` pair the row template needs.
 *
 * @param {Object|null|undefined} obj Container to inspect (anything non-object ⇒ false).
 * @param {string} key Row name, e.g. 'deduction' / 'net' / 'netTotal' / 'total' / 'gross'.
 * @returns {boolean}
 */
export function hasObjectRow(obj, key) {
  if (!obj || typeof obj !== 'object') return false;
  if (!Object.prototype.hasOwnProperty.call(obj, key)) return false;
  const v = obj[key];
  // 🔴 `typeof [] === 'object'` and `Boolean([]) === true`, so an array would otherwise
  // pass and the caller would render `row.label` (undefined ⇒ blank) next to
  // `formatAmount(row.amount)` (⇒ em dash): a ghost row claiming a deduction exists
  // whose value is unknown. A row is an OBJECT with `{ label, amount }`, never a list.
  if (Array.isArray(v)) return false;
  return Boolean(v) && typeof v === 'object';
}

/**
 * `expanded` comes off the wire as a boolean OR as the string `"true"`/`"True"`
 * (the template substitutes text into the slot), so a bare truthiness test would
 * also open every card carrying the string `"false"`.
 *
 * @param {*} v
 * @returns {boolean}
 */
export function isTruthy(v) {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  const s = String(v == null ? '' : v).trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'yes';
}

/**
 * 🔴 null / undefined / anything non-numeric ⇒ em dash. NEVER 0 — and, just as
 * important, a real `0` ⇒ "0.00" and never the em dash. Numbers arrive as
 * `1120000.0` (Newtonsoft decimal writer) and occasionally as their string form,
 * both of which format identically (grouped, 2 decimals, Arabic digits).
 *
 * @param {*} v
 * @returns {string} Formatted amount, or `EM_DASH` when the value is not a finite number.
 */
export function formatAmount(v) {
  if (v === null || v === undefined) return EM_DASH;
  let n = NaN;
  if (typeof v === 'number') n = v;
  else if (typeof v === 'string' && v.trim() !== '') n = Number(v);
  if (!Number.isFinite(n)) return EM_DASH;
  return n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * The sign a row asked to be printed IN FRONT of its own figure — `'+'` or `''`.
 *
 * 🔴 A GLYPH, NOT ARITHMETIC, and it is the opposite half of the rule `sumSignedAt` follows
 * in the card component. There a breakdown row states its direction in `tone` and the
 * renderer prints the minus; here the payload asks for the sign OUT LOUD with `signed: true`
 * because the figure is a CHANGE the customer is being quoted — "ต้องจ่ายเพิ่ม +62,175" is a
 * different sentence from "ต้องจ่ายเพิ่ม 62,175", which reads as the whole bill.
 *
 * 🔴 Never a second sign. Such a figure is authored positive (the backend sends the absolute
 * difference), but a hand-edited payload can hold a negative one — `formatAmount` already
 * prints its `-`, so this answers `''` for it. `0` gets no sign either: "+0" claims a
 * direction that a zero does not have.
 *
 * @param {Object|null|undefined} row A row carrying `{ value, signed }`.
 * @returns {string} `'+'` or `''` — never `'-'`, which belongs to the formatted number.
 */
export function amountSign(row) {
  if (!row || typeof row !== 'object' || !isTruthy(row.signed)) return '';
  const v = row.value;
  let n = NaN;
  if (typeof v === 'number') n = v;
  else if (typeof v === 'string' && v.trim() !== '') n = Number(v);
  return Number.isFinite(n) && n > 0 ? '+' : '';
}

// ── RULE 4 — PRESENTATION HINTS ─────────────────────────────────────────────────────
//
// The app's card contract carries roughly a hundred optional style hints, always in one
// of three spellings: `<name>Color` (a hex string) beside `<name>FontSize` (a number in
// Android **sp**) beside `<name>TextAlign` (one of the CSS keywords below), plus a handful
// of standalone fills — `mainFrameColor`, `rowFrameColor`, `frameColor`, `pillColor`,
// `tagFrameColor`, `capFrameColor`, `circleColor`, `dividerColor`, `accentColor`.
// See `tax-card-types.md`.
//
// 🔴 THE PREVIEW HONOURS THEM FOR ONE REASON: this screen answers "what will the customer
// see". A backend that can now ship a palette, next to a preview that keeps drawing its
// own, turns every styling change into something nobody can check before it reaches a
// phone — which is the same gap the card renderer itself exists to close.
//
// 🔴 EVERY VALUE IS VALIDATED HERE AND NOWHERE ELSE UPSTREAM. The backend carries hints
// verbatim on purpose (`TaxCardStyleV2`) and the app drops what `Color.parseColor` cannot
// read; a browser is more forgiving than that, so `color: "red"` or a stray `';'` would
// render HERE and silently not on the phone. Anything that is not `#RRGGBB` / `#AARRGGBB`
// is therefore ignored, which makes the preview's palette exactly the app's.

/** `#RRGGBB` or `#AARRGGBB` — the two forms `Color.parseColor` accepts and the app documents. */
const HINT_COLOR = /^#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** A font size the app would accept: a finite positive number. `0` hides text and is refused. */
function hintSize(v) {
  const n = typeof v === 'number' ? v : (typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * The alignments a `<name>TextAlign` hint may ask for.
 *
 * 🔴 A CLOSED LIST, for the same reason `HINT_COLOR` is a regex and not a pass-through: the
 * value lands in a `:style` binding, so anything a browser happens to understand would render
 * HERE and not on a phone — `justify` wraps Thai copy differently from every real client, and a
 * typo like `centre` would silently do nothing while looking authored.
 */
const HINT_ALIGN = ['left', 'right', 'center', 'start', 'end'];

/**
 * One alignment hint as CSS, or null.
 *
 * @param {*} v
 * @returns {?string} A CSS `text-align` keyword, or null when it is not one the app would accept.
 */
function hintAlign(v) {
  const s = String(v == null ? '' : v).trim().toLowerCase();
  return HINT_ALIGN.includes(s) ? s : null;
}

/**
 * One hex hint as CSS, or null.
 *
 * 🔴 `#AARRGGBB` is REORDERED, not passed through. Android puts alpha first and CSS puts
 * it last (`#RRGGBBAA`), so shipping the string unchanged paints a card the wrong colour
 * AND at the wrong opacity — and it looks deliberate, which is the worst kind of wrong.
 *
 * @param {*} v
 * @returns {?string} A CSS colour, or null when the value is not one the app would accept.
 */
export function hintColor(v) {
  const s = String(v == null ? '' : v).trim();
  if (!HINT_COLOR.test(s)) return null;
  if (s.length !== 9) return s;
  return '#' + s.slice(3) + s.slice(1, 3);          // #AARRGGBB → #RRGGBBAA
}

/**
 * The inline style for one run of text: `<prefix>Color` + `<prefix>FontSize` +
 * `<prefix>TextAlign` on `src`.
 *
 * 🔴 Returns null rather than `{}` when nothing was authored, so a `:style` binding adds
 * no attribute at all and the stylesheet below keeps deciding — a card nobody styled must
 * render byte for byte as it did before hints existed.
 *
 * @param {Object|null|undefined} src The payload object carrying the hints.
 * @param {string} prefix The hint name without its `Color` / `FontSize` / `TextAlign` suffix,
 *   e.g. 'title'.
 * @returns {?Object} A Vue style object, or null.
 */
export function textStyle(src, prefix) {
  if (!src || typeof src !== 'object' || !prefix) return null;
  const style = {};
  const color = hintColor(src[prefix + 'Color']);
  if (color) style.color = color;
  const size = hintSize(src[prefix + 'FontSize']);
  // sp → px 1:1. An honest approximation: sp is Android's scale-independent pixel and a
  // browser has no equivalent, so the preview shows the RATIO the designer chose rather
  // than claiming to reproduce the phone's accessibility scaling.
  if (size) style.fontSize = size + 'px';
  // 🔴 The third hint exists because a caption's SIDE is authored copy, not decoration: the
  //    app right-aligns `noteAbove` over its box and centres the odd `note`, and a preview
  //    that pinned both to the left would disagree with the phone on every such row.
  const align = hintAlign(src[prefix + 'TextAlign']);
  if (align) style.textAlign = align;
  return Object.keys(style).length ? style : null;
}

/**
 * The inline style for a standalone colour hint — a frame, a pill, a divider.
 *
 * @param {Object|null|undefined} src The payload object carrying the hint.
 * @param {string} key The hint's full key, e.g. 'mainFrameColor' / 'rowFrameColor'.
 * @param {string} [prop] The CSS property it paints. Defaults to `backgroundColor`.
 * @returns {?Object} A Vue style object, or null.
 */
export function fillStyle(src, key, prop) {
  if (!src || typeof src !== 'object' || !key) return null;
  const color = hintColor(src[key]);
  return color ? { [prop || 'backgroundColor']: color } : null;
}

/**
 * Several hint styles as one. Later arguments win a property earlier ones set, which is
 * how a row's own hint overrides the card-wide default it sits inside.
 *
 * @param {...(Object|null|undefined)} styles
 * @returns {?Object} The merged style object, or null when every argument was empty.
 */
export function mergeStyles(...styles) {
  const out = {};
  styles.forEach((s) => {
    if (s && typeof s === 'object') Object.assign(out, s);
  });
  return Object.keys(out).length ? out : null;
}

/**
 * The spacing hints a type7 (`TAX_AMOUNT_FORM`) field may carry — the app's own names,
 * numbers in Android **dp**, each optional. They ride flat on the field object because the
 * backend merges `fields[].style` down onto it (`TaxShortFormCardV2.ToBuiltInJson`).
 *
 * 🔴 A CLOSED LIST, like `HINT_ALIGN`: the value lands in a `:style` binding, and a browser
 * would happily honour `margin` / `gap` / `marginStart` that no phone reads.
 */
const SPACING_KEYS = ['marginTop', 'marginBottom', 'paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight'];

/**
 * The inline spacing for one field row: every `SPACING_KEYS` entry on `src` that holds a
 * finite number ≥ 0, as `<n>px`.
 *
 * 🔴 NUMBERS ONLY — stricter than `hintSize`, which also reads `"14"`. The app's contract
 * types these as numbers and the wire carries them as numbers (pinned in BAL.Tests), so a
 * string here is an authoring mistake the phone would drop; drawing it would be the preview
 * lying about the phone. `0` is a real value ("no gap") and becomes `'0px'`; a negative,
 * `NaN`, `Infinity`, `null` or text is ignored, never clamped.
 *
 * 🔴 Returns `{}` rather than null when nothing was authored — the binding site merges it
 * with `mergeStyles`, which skips an empty object, and a bare `:style="{}"` adds no
 * attribute — so a row nobody spaced keeps the stylesheet's layout byte for byte.
 *
 * dp → px 1:1, the same honest approximation `textStyle` makes for sp.
 *
 * @param {Object|null|undefined} src The field object carrying the hints.
 * @returns {Object} A Vue style object — empty when no valid hint was found.
 */
export function spacingStyle(src) {
  const style = {};
  if (!src || typeof src !== 'object') return style;
  SPACING_KEYS.forEach((key) => {
    const v = src[key];
    if (typeof v === 'number' && Number.isFinite(v) && v >= 0) style[key] = v + 'px';
  });
  return style;
}
